import { useState } from "react";
import { Loader2, Lock, Send, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface AdminReservation {
  id: string;
  pod_label: string;
  status: string;
  access_code: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  date_label: string;
  from_label: string;
  to_label: string;
  duration_label: string;
  amount_label: string;
}

const ReservationsAdmin = () => {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reservations, setReservations] = useState<AdminReservation[]>([]);
  const [codeDrafts, setCodeDrafts] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState("");

  const call = async (body: Record<string, unknown>) => {
    const { data, error: fnError } = await supabase.functions.invoke("manage-reservations", {
      body: { password, ...body },
    });
    if (fnError || data?.error) {
      throw new Error(data?.error || "Error - contact support");
    }
    return data;
  };

  const load = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await call({ action: "list" });
      setReservations(data.reservations ?? []);
      setUnlocked(true);
    } catch (err) {
      setError((err as Error).message);
    }
    setLoading(false);
  };

  const sendCode = async (id: string) => {
    setBusyId(id);
    setError("");
    try {
      await call({ action: "set_code", reservation_id: id, access_code: codeDrafts[id] ?? "" });
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
    setBusyId("");
  };

  const cancel = async (id: string) => {
    setBusyId(id);
    setError("");
    try {
      await call({ action: "cancel", reservation_id: id });
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
    setBusyId("");
  };

  if (!unlocked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <form onSubmit={load} className="w-full max-w-sm rounded-2xl border border-border bg-card p-8">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <h1 className="mb-6 text-center font-funnel text-xl font-semibold text-primary">
            Reservations
          </h1>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full rounded-xl border border-border bg-background px-4 py-3 focus:border-primary focus:outline-none"
          />
          {error && (
            <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !password}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-medium text-primary-foreground disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Open
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-12 tablet:px-8">
      <div className="mx-auto max-w-4xl">
        <h1 className="font-funnel text-3xl font-semibold text-primary">Pod reservations</h1>
        <p className="mt-2 text-foreground/70">
          Paid reservations automatically show the reserved page on the pod link.
        </p>

        {error && (
          <div className="mt-6 rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="mt-8 space-y-4">
          {reservations.length === 0 && (
            <p className="text-foreground/60">No reservations yet.</p>
          )}
          {reservations.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border bg-card p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-funnel text-lg font-semibold text-primary">
                    {r.date_label}, {r.from_label} - {r.to_label}
                  </p>
                  <p className="mt-1 text-sm text-foreground/70">
                    {r.pod_label} &middot; {r.duration_label} &middot; {r.amount_label}
                  </p>
                  <p className="mt-2 text-sm text-foreground/70">
                    {r.customer_name} &middot; {r.customer_email}
                    {r.customer_phone ? ` · ${r.customer_phone}` : ""}
                  </p>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    r.status === "paid"
                      ? "bg-primary/10 text-primary"
                      : r.status === "cancelled"
                        ? "bg-destructive/10 text-destructive"
                        : "bg-muted text-foreground/60"
                  }`}
                >
                  {r.status}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <input
                  type="text"
                  value={codeDrafts[r.id] ?? (r.access_code?.startsWith("Assigned privately") ? "" : r.access_code) ?? ""}
                  onChange={(e) => setCodeDrafts({ ...codeDrafts, [r.id]: e.target.value })}
                  placeholder="Access code"
                  className="w-40 rounded-xl border border-border bg-background px-4 py-2.5 focus:border-primary focus:outline-none"
                />
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => sendCode(r.id)}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  {busyId === r.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  Save and email code
                </button>
                {r.status !== "cancelled" && (
                  <button
                    type="button"
                    disabled={busyId === r.id}
                    onClick={() => cancel(r.id)}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm text-foreground/70 hover:bg-muted disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4" /> Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReservationsAdmin;
